import {
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import {
  SELECT_PROP_TYPES,
  SELECT_AUTOCOMPLETE_PROP_TYPES,
  SELECT_MENU_PROP_TYPES,
  SELECT_MULTIPLE_PROP_TYPES,
  SELECT_MULTIPLE_AUTOCOMPLETE_PROP_TYPES,
} from './constants';
import { useState } from 'react';

function SelectAutocomplete(props) {
  const {
    disabled,
    error,
    events = {},
    label,
    name,
    helperText,
    size,
    value,
    onBlur,
    onChange,
  } = props;
  let { options = [] } = props;

  if (typeof options === 'function') {
    options = options();
  }

  return (
    <Autocomplete
      disabled={disabled}
      onChange={(event, value) => {
        const target = {
          name,
          value: value?.id,
        };

        onChange({
          target,
        });

        if (events.onChange) {
          events.onChange(target);
        }
      }}
      onBlur={onBlur}
      options={options}
      getOptionLabel={({ name }) => name}
      groupBy={({ groupBy }) => groupBy}
      defaultValue={options.find(_ => String(_.id) === String(value))}
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          fullWidth
          size={size}
          error={error}
          helperText={helperText}
          label={label}
          variant="outlined"
        />)}
    />);
}

function SelectMenu(props) {
  const {
    disabled,
    emptyElement,
    error,
    events = {},
    group,
    helperText,
    label,
    name,
    onBlur,
    onChange,
    size,
    value,
  } = props;
  let { options = [] } = props;

  if (typeof options === 'function') {
    options = options();
  }

  const groups = group ? [...new Set(
    options.map(({ groupBy }) => groupBy),
  )] : [];

  return (
    <TextField
      autoComplete={true}
      disabled={disabled}
      error={error}
      fullWidth={true}
      helperText={helperText}
      InputLabelProps={{
        shrink: true,
      }}
      label={label}
      name={name}
      select={true}
      SelectProps={{
        native: true,
      }}
      size={size}
      variant="outlined"
      value={value}
      onBlur={onBlur}
      onChange={(e) => {
        onChange(e);

        if (events.onChange) {
          events.onChange(e?.target);
        }
      }}
    >
      {emptyElement && (<option>{emptyElement}</option>)}
      {group && (groups.map((item, index) => {
        const opt = options.filter((_) => _.groupBy === item);

        return (opt.length ? <optgroup key={index} label={item} style={{
          fontStyle: 'normal',
        }}>
          {opt.map(({ id, name }, key) => (
            <option value={id} key={key}>
              {name}
            </option>
          ))}
        </optgroup> : <></>);
      }))}
      {!group && options.map(({ id, name }, key) => (
        <option value={id} key={key}>
          {name}
        </option>
      ))}
    </TextField>
  );
}

function SelectMultiple(props) {
  const {
    disabled,
    error,
    events = {},
    helperText,
    label,
    name,
    onChange,
    size,
  } = props;
  let { options = [], value = [] } = props;

  if (!Array.isArray(value)) {
    value = [];
  }

  if (typeof options === 'function') {
    options = options();
  }

  return (<FormControl
    fullWidth
    size={size}
    variant="outlined"
  >
    <InputLabel>{label}</InputLabel>
    <Select
      error={error}
      id="demo-mutiple-checkbox"
      input={<OutlinedInput label={label} />}
      multiple
      name={name}
      onChange={(e) => {
        onChange(e);

        if (events.onChange) {
          events.onChange(e?.target);
        }
      }}
      renderValue={(selected) => `${selected.length} seleccionado${selected.length !== 1 ? 's' : ''}`}
      value={value}
      variant="outlined"
    >
      {options.map((option, index) => (
        <MenuItem key={index} value={option.id}>
          {!disabled && (<Checkbox
            checked={value.includes(option.id)}
          />)}
          <ListItemText primary={option.name} />
        </MenuItem>
      ))}
    </Select>
    <FormHelperText children={helperText} error />
  </FormControl>);
}

function SelectMultipleAutocomplete(props) {
  const {
    disabled,
    error,
    events = {},
    helperText,
    label,
    name,
    onChange,
    onBlur,
    size,
  } = props;
  let { options = [], value = [] } = props;

  if (!Array.isArray(value)) {
    value = [];
  }

  if (typeof options === 'function') {
    options = options();
  }

  const [search, setSearch] = useState('');

  // Filtra opciones en función de la búsqueda
  const filteredOptions = options.filter((option) =>
    option.name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <FormControl fullWidth size={size} variant="outlined">
      <Autocomplete
        multiple
        disabled={disabled}
        options={filteredOptions}
        value={options.filter((option) => value.includes(option.id))}
        onChange={(_event, newValue) => {
          const selectedIds = newValue.map((option) => option.id);
          const target = {
            name,
            value: selectedIds,
          };

          onChange({
            target,
          });

          if (events.onChange) {
            events.onChange(target);
            if (typeof target.value === 'string') {
              setSearch(target.value);
            }
          }
        }}
        filterOptions={(options, params) => {
          const filtered = options.filter((option) =>
            option.name.toLowerCase().includes(params.inputValue.toLowerCase()),
          );
          return filtered;
        }}
        getOptionLabel={(option) => option.name}
        renderOption={(props, option, { selected } = {}) => {
          return (
            <li {...props} key={option.id}>
              <ListItemText primary={props.name} />
            </li>
          );
        }}

        renderInput={(params) => (
          <TextField
            {...params}
            name={name}
            fullWidth
            size={size}
            error={error}
            helperText={helperText}
            label={label}
            variant="outlined"
            // onChange={(e) => (console.debug(e.target.value), setSearch(e.target.value))}
            onBlur={onBlur}
          />
        )}
      />
      <FormHelperText children={helperText} error />
    </FormControl>
  );
}

function SelectControl(props) {
  const {
    disabled,
    label,
    name,
    settings: {
      autocomplete = false,
      emptyElement,
      group = false,
      multiple = false,
      options = [],
    },
    events = {},
    size,
    value,
    error,
    helperText,
    onBlur,
    onChange,
  } = props;
  const Control = multiple && autocomplete
    ? SelectMultipleAutocomplete :
    multiple ? SelectMultiple
      : (autocomplete ? SelectAutocomplete : SelectMenu);

  return (
    <Control
      disabled={disabled}
      emptyElement={emptyElement}
      error={error}
      events={events}
      group={group}
      helperText={helperText}
      label={label}
      multiple={multiple}
      name={name}
      onBlur={onBlur}
      onChange={onChange}
      options={options}
      size={size}
      value={value}
    />
  );
}

SelectAutocomplete.propTypes = SELECT_AUTOCOMPLETE_PROP_TYPES;
SelectMenu.propTypes = SELECT_MENU_PROP_TYPES;
SelectMultiple.propTypes = SELECT_MULTIPLE_PROP_TYPES;
SelectMultipleAutocomplete.propTypes = SELECT_MULTIPLE_AUTOCOMPLETE_PROP_TYPES;
SelectControl.propTypes = SELECT_PROP_TYPES;

SelectControl.defaultProps = {
  disabled: false,
  settings: {
    autocomplete: false,
    group: false,
    options: [],
  },
  size: 'small',
  onChange: () => { },
  onBlur: () => { },
};

export default SelectControl;
